import { Box, Button, Theme, useMediaQuery } from '@mui/material';

import React, { useState } from 'react';
import { CountedProductFilters } from 'generated/graphql';
import { filterCategories } from 'constants/filterCategories';
import { useProductsContext } from 'stores/useProducts';
import { useServerTranslation } from 'hooks/useServerTranslation';
import FilterCategory from './components/FilterCategory';
import ProductFilterHeader from './components/ProductFilterHeader';
import ConditionalCollapse from './components/ConditionalCollapse';
import ProductSorting from './components/ProductSorting';

type ProductFiltersProps = { searchValue?: string };

export default function ProductFilters({ searchValue }: ProductFiltersProps) {
    const { t } = useServerTranslation();
    const showingProductsAmount = useProductsContext((store) => store.showing);
    const productFilters = useProductsContext((store) => store.productFilters);
    const resetFilters = useProductsContext((store) => store.resetFilters);
    const [showFilters, setShowFilters] = useState(false);
    const isLargeScreen = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'));

    const toggleFilters = () => {
        setShowFilters((show) => !show);
    };

    return (
        <Box marginRight={[null, null, 5]} minWidth="auto" sx={{ marginTop: 2 }}>
            <ProductFilterHeader
                productAmount={showingProductsAmount}
                toggle={toggleFilters}
                showing={showFilters}
            />
            <ConditionalCollapse collapse={!isLargeScreen} showing={showFilters}>
                <Box maxWidth="400px" marginX="auto">
                    <ProductSorting searchValue={searchValue} />
                    <RenderProductFilters {...productFilters} />
                    <Button
                        variant="black"
                        color="primary"
                        onClick={resetFilters}
                        sx={{
                            marginLeft: 'auto',
                            marginTop: 1,
                            display: 'block',
                            border: '0.5px solid',
                            borderRadius: '4px',
                        }}
                    >
                        {t('product-filter.reset')}
                    </Button>
                </Box>
            </ConditionalCollapse>
        </Box>
    );
}

function RenderProductFilters(productFilters: Omit<CountedProductFilters, '__typename'>) {
    return (
        <>
            {Object.keys(productFilters).map((key) => {
                if (isKeyOfCountedProductFilters(key)) {
                    const { [key]: filters } = productFilters;

                    return <FilterCategory key={key} title={key} filters={filters} />;
                }

                return null;
            })}
        </>
    );
}

function isKeyOfCountedProductFilters(key: string): key is keyof Omit<CountedProductFilters, '__typename'> {
    return filterCategories.includes(key);
}
